/**
 * The Auth.js cookie bridge — a TRANSITIONAL shim, and the first thing to
 * delete when v2.2 finishes.
 *
 * ── Why it exists ─────────────────────────────────────────────────────────
 * The SPA authenticates with FastAPI-issued tokens (FE-04). But half the API
 * is still Next.js routes, and those authenticate through `requireAuth()` ->
 * `auth()` -> the **Auth.js session cookie**. They do not look at
 * `Authorization` at all. A browser holding only a bearer token would get a
 * clean 401 from `/api/dashboard` — the SPA would render and show nothing.
 *
 * So on login the SPA establishes BOTH credentials: the token pair (for
 * `/py-api/v1/*`) and an Auth.js cookie (for the `/api/*` routes not yet
 * ported). `apiFetch` sends both on every request and each backend takes the
 * one it understands.
 *
 * ── How ───────────────────────────────────────────────────────────────────
 * Through Auth.js's own public HTTP surface, exactly as `next-auth/react`'s
 * `signIn()` does it — no private internals, no new Next.js route (D-001
 * forbids adding one):
 *
 *   1. `GET  /api/auth/csrf`                  -> `{ csrfToken }`
 *   2. `POST /api/auth/callback/credentials`  form-encoded, with that token
 *      and `X-Auth-Return-Redirect: 1` so Auth.js answers `{ url }` as JSON
 *      instead of a 302.
 *
 * A URL carrying `?error=` means the callback rejected the credentials.
 *
 * ── Failure policy: NON-FATAL ─────────────────────────────────────────────
 * The bridge failing must not block sign-in. The token pair is the real
 * session; the cookie is a compatibility crutch. If Next.js is down, or has
 * already been removed, the FastAPI-backed pages must still work. Callers get
 * a result object, never an exception, and the SPA surfaces a degraded-mode
 * warning rather than refusing to log the user in.
 *
 * ── Deleting it ───────────────────────────────────────────────────────────
 * When `API_DOMAINS` has no `backend: 'nextjs'` entries left, set
 * `VITE_LEGACY_NEXTAUTH_BRIDGE=off`, confirm nothing breaks, then delete this
 * file, its call in `AuthProvider`, and the `credentials: 'include'` line in
 * `session.ts`.
 */

import { API_DOMAINS, type ApiBackend } from "@/lib/api/config";

export type LegacyBridgeOutcome = "ok" | "rejected" | "unavailable" | "disabled";

export interface LegacyBridgeResult {
  outcome: LegacyBridgeOutcome;
  /** Safe to show in a degraded-mode banner; null when `outcome === "ok"`. */
  message: string | null;
}

const CSRF_URL = "/api/auth/csrf";
const CALLBACK_URL = "/api/auth/callback/credentials";

/**
 * On only while some domain is still served by Next.js.
 *
 * DERIVED from `API_DOMAINS` rather than defaulted by hand, because this
 * module's own header names that exact condition as the moment to switch it
 * off -- and the condition became true at the CUT-01/CUT-02 cutover without
 * anybody flipping a flag. The consequence was visible in production on
 * 2026-08-22: `/api/auth/csrf` no longer exists, so it falls through to the
 * SPA catch-all and answers 200 with HTML, the bridge reads that as a failure,
 * and EVERY user was shown "Signed in, but the legacy pages could not be
 * reached." on every login -- an alarm about a subsystem that had been
 * deliberately deleted. A false alarm shown to all eight people is worse than
 * no alarm: it teaches them to ignore the banner that will one day be real.
 *
 * Deriving it means the switch cannot rot again in either direction. Restore a
 * `backend: "nextjs"` domain and the bridge comes back on by itself.
 *
 * `VITE_LEGACY_NEXTAUTH_BRIDGE` still overrides, both ways, for the transition
 * window: "off" forces it off, "on" forces it on.
 */
export function legacyBridgeEnabled(): boolean {
  const override = import.meta.env?.VITE_LEGACY_NEXTAUTH_BRIDGE;
  if (override === "off") return false;
  if (override === "on") return true;
  // Widened to `ApiBackend` deliberately. Against the literal types of
  // API_DOMAINS this comparison is provably false today -- tsc says
  // '"fastapi"' and '"nextjs"' have no overlap -- which is exactly the proof
  // that the bridge is dead weight. Widening keeps that a runtime question, so
  // restoring a `backend: "nextjs"` domain revives the bridge instead of
  // silently leaving it off.
  const backends: ApiBackend[] = Object.values(API_DOMAINS).map((d) => d.backend);
  return backends.includes("nextjs");
}

export interface LegacyBridgeOptions {
  fetchImpl?: typeof fetch;
  enabled?: boolean;
  /** Origin prefix for the Next.js app. Same origin by default. */
  host?: string;
}

/**
 * Establish an Auth.js session cookie for `email`/`password`.
 *
 * The password is sent a second time, to a second backend. That is worth
 * naming: it is the same password over the same TLS connection to the same
 * origin the user already trusts with it, and it is the only way to obtain a
 * cookie that Next.js's own `auth()` will accept. It stops the day the last
 * Next.js route is ported.
 */
export async function establishLegacySession(
  email: string,
  password: string,
  options: LegacyBridgeOptions = {},
): Promise<LegacyBridgeResult> {
  const enabled = options.enabled ?? legacyBridgeEnabled();
  if (!enabled) return { outcome: "disabled", message: null };

  const doFetch = options.fetchImpl ?? fetch;
  const host = (options.host ?? "").replace(/\/+$/, "");

  let csrfToken: string;
  try {
    const res = await doFetch(`${host}${CSRF_URL}`, {
      credentials: "include",
      headers: { accept: "application/json" },
    });
    if (!res.ok) {
      return {
        outcome: "unavailable",
        message: "Signed in, but the legacy pages could not be reached.",
      };
    }
    const body = (await res.json()) as { csrfToken?: unknown };
    if (typeof body?.csrfToken !== "string" || !body.csrfToken) {
      return {
        outcome: "unavailable",
        message: "Signed in, but the legacy pages could not be reached.",
      };
    }
    csrfToken = body.csrfToken;
  } catch {
    return {
      outcome: "unavailable",
      message: "Signed in, but the legacy pages could not be reached.",
    };
  }

  try {
    const form = new URLSearchParams({
      csrfToken,
      email,
      password,
      callbackUrl: "/dashboard",
      redirect: "false",
    });
    const res = await doFetch(`${host}${CALLBACK_URL}`, {
      method: "POST",
      credentials: "include",
      // Tells Auth.js to answer `{ url }` as JSON rather than issuing a 302
      // the SPA would have to follow. Same header `next-auth/react` sends.
      headers: {
        "content-type": "application/x-www-form-urlencoded",
        "x-auth-return-redirect": "1",
        accept: "application/json",
      },
      body: form.toString(),
    });

    if (!res.ok) {
      return {
        outcome: "unavailable",
        message: "Signed in, but the legacy pages could not be reached.",
      };
    }

    // A rejected credential still answers 200 — the failure is encoded in the
    // returned URL, exactly as it is for `next-auth/react`.
    let url: string | null = null;
    try {
      const body = (await res.json()) as { url?: unknown };
      if (typeof body?.url === "string") url = body.url;
    } catch {
      url = null;
    }
    if (url && /[?&]error=/.test(url)) {
      return {
        outcome: "rejected",
        message: "Signed in, but the legacy pages rejected the same credentials.",
      };
    }
    return { outcome: "ok", message: null };
  } catch {
    return {
      outcome: "unavailable",
      message: "Signed in, but the legacy pages could not be reached.",
    };
  }
}

/** Best-effort Auth.js cookie teardown on sign-out. Never throws. */
export async function clearLegacySession(options: LegacyBridgeOptions = {}): Promise<void> {
  if (!(options.enabled ?? legacyBridgeEnabled())) return;
  const doFetch = options.fetchImpl ?? fetch;
  const host = (options.host ?? "").replace(/\/+$/, "");
  try {
    const res = await doFetch(`${host}${CSRF_URL}`, {
      credentials: "include",
      headers: { accept: "application/json" },
    });
    if (!res.ok) return;
    const body = (await res.json()) as { csrfToken?: unknown };
    if (typeof body?.csrfToken !== "string") return;
    await doFetch(`${host}/api/auth/signout`, {
      method: "POST",
      credentials: "include",
      headers: {
        "content-type": "application/x-www-form-urlencoded",
        "x-auth-return-redirect": "1",
      },
      body: new URLSearchParams({ csrfToken: body.csrfToken, callbackUrl: "/login" }).toString(),
    });
  } catch {
    // A cookie we cannot clear expires on its own; the token session is
    // already gone, which is what actually signs the user out.
  }
}
